import React, { forwardRef, useMemo, useRef } from "react";
import { alpha, margin } from "@mui/system";

/* types */

import { MenuList, ListItemIcon, ListItemText, ListSubheader, Divider, Box, Typography } from "@mzc-pdc/ui";

/* styled */
import { Popover, MenuItem } from "./styled";
import { IconButton, Button, Tooltip } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import { IconArrowDropDown, IconArrowDropUp } from "@mzc-cloudplex/icons";
import { useTranslation } from "react-i18next";

const getTextWidth = (title) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (context) {
        context.font = "14px Noto Sans KR";
        const width = context.measureText(title).width;
        return width;
    }
    return 0;
};

export const Menu = forwardRef(
    (
        {
            target,
            menuWidth,
            menuMargin,
            menuMinWidth,
            placement,
            type,
            startIcon,
            endIcon,
            endOpenIcon,
            label,
            color,
            size,
            variant,
            list,
            listSize,
            actions,
            divider,
            preventPropagation = true,
            useTooltip = false,
            value,
            useInCard,
            sx = {},
            ...restProps
        },
        ref,
    ) => {
        const [anchorEl, setAnchorEl] = React.useState(null);
        const testRef = useRef(null);
        const open = Boolean(anchorEl);
        const handleClick = (event) => {
            if (preventPropagation) event.stopPropagation();
            setAnchorEl(event.currentTarget);
        };
        const handleClose = (event) => {
            if (preventPropagation) event.stopPropagation();
            setAnchorEl(null);
        };

        const iconButtonStyleInCard = useMemo(() => {
            if (!useInCard || type !== "icon") return { ...sx };
            return {
                ...sx,
                backgroundColor: alpha(theme.palette.constant.black, 0.6),
                color: theme.palette.constant.white,
                "&:hover": {
                    backgroundColor: alpha(theme.palette.constant.black, 0.8),
                },
                '&[aria-expanded="true"]': {
                    backgroundColor: alpha(theme.palette.constant.black, 0.92),
                },
            };
        }, [useInCard]);

        if (!list?.length) return null;

        return (
            <>
                {type === "icon" ? (
                    <IconButton
                        ref={ref}
                        id="menu-button"
                        aria-controls={open ? "menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={handleClick}
                        color={color}
                        size={size}
                        circled
                        active={open.toString()}
                        disabled={
                            restProps.disabled || list?.filter((item) => !item.hidden && !item.groupLabel)?.length === 0
                        }
                        sx={iconButtonStyleInCard}
                        {...restProps}
                    >
                        {label}
                    </IconButton>
                ) : (
                    <Button
                        ref={ref}
                        id="menu-button"
                        aria-controls={open ? "menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={handleClick}
                        color={color}
                        size={size}
                        variant={variant}
                        startIcon={startIcon}
                        endIcon={open ? endOpenIcon : endIcon}
                        disabled={
                            restProps.disabled || list?.filter((item) => !item.hidden && !item.groupLabel)?.length === 0
                        }
                        sx={sx}
                        {...restProps}
                    >
                        {label}
                    </Button>
                )}
                <Popover
                    id="menu-popover"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={(() => {
                        const placementValue = placement ?? "left";
                        if (placementValue === "top" || placementValue === "bottom") {
                            return {
                                vertical: placementValue,
                                horizontal: "left",
                            };
                        }
                        return {
                            vertical: "bottom",
                            horizontal: placementValue,
                        };
                    })()}
                    transformOrigin={(() => {
                        const placementValue = placement ?? "left";
                        if (placementValue === "top" || placementValue === "bottom") {
                            return {
                                vertical: placementValue === "top" ? "bottom" : "top",
                                horizontal: "left",
                            };
                        }
                        return {
                            vertical: "top",
                            horizontal: placementValue,
                        };
                    })()}
                    PaperProps={{
                        style: {
                            minWidth: `${menuMinWidth}px`,
                            width: `${menuWidth}px`,
                            margin: `${menuMargin}px`,
                            border: `1px solid ${theme.palette.greySecondary[100]}`,
                        },
                    }}
                >
                    <MenuList id="menu-list">
                        {list
                            ?.filter((item) => !item.hidden)
                            .map((item, index) => {
                                if (item.groupLabel) {
                                    return [
                                        item.divider && <Divider key={`divider-${item.value}`} />,
                                        <ListSubheader
                                            key={`group-label-${item.value}`}
                                            disableSticky
                                            sx={{
                                                height: "34px",
                                                display: "flex",
                                                alignItems: "center",
                                                px: "20px",
                                                cursor: "default",
                                                lineHeight: "normal",
                                                backgroundColor: "transparent",
                                            }}
                                        >
                                            <Typography variant={"body3_500"} color={theme.palette.greySecondary[600]}>
                                                {item.label}
                                            </Typography>
                                        </ListSubheader>,
                                    ];
                                }
                                const width = getTextWidth(item.label);
                                return [
                                    item.divider && <Divider key={`divider-${item.value}`} />,
                                    <Tooltip key={item.value} title={useTooltip && width >= 274 ? item.label : ""}>
                                        <MenuItem
                                            ref={testRef}
                                            id="menu-item"
                                            key={item.label}
                                            disabled={item.disabled}
                                            selected={item.selected}
                                            disableGutters
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (item.onClick) item.onClick(item.value, value);
                                                if (item.clickClose) handleClose(e);
                                            }}
                                            sx={{
                                                height: listSize === "small" ? "32px" : "40px",
                                                "&:hover": {
                                                    backgroundColor: alpha(theme.palette.secondary[800], 0.08),
                                                },
                                                "&:active": {
                                                    backgroundColor: alpha("#c9d4f4", 0.04),
                                                },
                                                "&.Mui-selected, &.Mui-selected:hover": {
                                                    backgroundColor: alpha("#c9d4f4", 0.4),
                                                },
                                            }}
                                        >
                                            {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                                            {item.control && <>{item.control}</>}
                                            <ListItemText
                                                primary={item.label}
                                                primaryTypographyProps={{
                                                    variant: "body1_400",
                                                    component: "span",
                                                    color: item.color ?? theme.palette.greySecondary.main,
                                                }}
                                            />
                                        </MenuItem>
                                    </Tooltip>,
                                ];
                            })}
                    </MenuList>
                    {actions && (
                        <div id={"menu-item-actions"} role={"menuitem"}>
                            {actions}
                        </div>
                    )}
                </Popover>
            </>
        );
    },
);

export const ActionMenu = ({ list, ...restProps }) => {
    const { t } = useTranslation();
    return (
        <Menu
            open
            menuMinWidth={160}
            endIcon={
                <Box
                    sx={{
                        width: `24px`,
                        height: `24px`,
                        borderRadius: `100%`,
                        display: `flex`,
                        alignItems: `center`,
                        justifyContent: `center`,
                        marginRight: `-10px`,
                    }}
                >
                    <IconArrowDropDown size={20} />
                </Box>
            }
            endOpenIcon={
                <Box
                    sx={{
                        width: `24px`,
                        height: `24px`,
                        borderRadius: `100%`,
                        display: `flex`,
                        alignItems: `center`,
                        justifyContent: `center`,
                        marginRight: `-10px`,
                        backgroundColor: alpha(theme.palette.secondary.light, 0.08),
                    }}
                >
                    <IconArrowDropUp size={20} />
                </Box>
            }
            label={t("common::label::Actions")}
            size={"medium"}
            variant={"outlined"}
            color={"greySecondary"}
            placement={"right"}
            list={list}
            {...restProps}
        />
    );
};

export default Menu;
